import {
  Alert,
  Badge,
  BodyText,
  Button,
  Card,
  EmptyState,
  METRIC_READ_CAPTIONS,
  MetricCard,
  Skeleton,
} from '@aaimplatform/ui';
import type { components as SyndicationComponents } from '@aaimplatform/institutional-types/generated/loan-syndication-protocol';
import { useParticipationPositions } from '../../hooks/useLoanSyndication';
import { enumLabel } from '../../utils/status-labels';
import {
  ABSENT,
  formatCount,
  formatDate,
  formatMoneyCompact,
  formatRatioAsPercent,
} from '../../utils/display-format';
import {
  POSITIONS_SCOPE_NOTE,
  POSITION_ROLE_LABEL,
  POSITION_STATUS_LABEL,
  collateralAssetClassLabel,
  collateralLtvLabel,
  positionYieldLabel,
  weightedAverageYield,
} from './syndication-display';

type ParticipationPosition = SyndicationComponents['schemas']['ParticipationPosition'];

function averageYieldDescription(activeCount: number, reportingCount: number): string {
  if (activeCount === 0) return 'No active positions';
  if (reportingCount === 0) return 'No active position reports a yield';
  if (reportingCount < activeCount) {
    return `Committed-weighted, ${formatCount(reportingCount)} of ${formatCount(activeCount)} active positions report a yield`;
  }
  return 'Committed-weighted across active positions';
}

function PositionCard({ position }: { position: ParticipationPosition }) {
  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <BodyText variant="ui" className="font-semibold font-mono text-sm break-all">
              {position.offeringId}
            </BodyText>
            <Badge variant="neutral">{enumLabel(POSITION_STATUS_LABEL, position.status)}</Badge>
            <Badge variant="neutral">{enumLabel(POSITION_ROLE_LABEL, position.role)}</Badge>
          </div>
          <BodyText variant="ui" className="text-tertiary">
            {position.institutionName ?? 'Institution not reported'}
            {' | '}
            {collateralAssetClassLabel(position.collateralSummary)}
          </BodyText>
        </div>
        <div className="text-right">
          <BodyText variant="ui" className="font-semibold">
            {formatMoneyCompact(position.committedAmount)}
          </BodyText>
          <BodyText variant="ui" className="text-tertiary">
            {formatRatioAsPercent(position.participationShare)} share
          </BodyText>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 pt-3 border-t border-recessed">
        <div>
          <BodyText variant="ui" className="text-tertiary text-xs">Yield</BodyText>
          <BodyText variant="ui" className="font-medium">
            {positionYieldLabel(position.annualizedYield)}
          </BodyText>
        </div>
        <div>
          <BodyText variant="ui" className="text-tertiary text-xs">Interest Earned</BodyText>
          <BodyText variant="ui" className="font-medium">
            {formatMoneyCompact(position.totalInterestReceived)}
          </BodyText>
        </div>
        <div>
          <BodyText variant="ui" className="text-tertiary text-xs">LTV Ratio</BodyText>
          <BodyText variant="ui" className="font-medium">
            {collateralLtvLabel(position.collateralSummary)}
          </BodyText>
        </div>
        <div>
          <BodyText variant="ui" className="text-tertiary text-xs">Next Distribution</BodyText>
          <BodyText variant="ui" className="font-medium">
            {/* format: date, a calendar day: formatted in UTC so a US viewer
                does not see the previous day. */}
            {position.nextDistributionDate
              ? formatDate(position.nextDistributionDate, { timeZone: 'UTC' })
              : ABSENT}
          </BodyText>
        </div>
      </div>
    </Card>
  );
}

export function SyndicationPositions() {
  const { data, isLoading, error, refetch, isFetching } = useParticipationPositions();

  const positions = data?.positions ?? [];
  const totalItems = data?.pagination.totalItems;
  const partial = typeof totalItems === 'number' && totalItems > positions.length;
  // The sums cover the positions this page read. When the service holds more
  // than one page, the description says so rather than presenting a page
  // total as the book total.
  const scopeNote = partial
    ? `Across the first ${formatCount(positions.length)} of ${formatCount(totalItems)} positions`
    : undefined;
  const totalCommitted = positions.reduce((sum, p) => sum + p.committedAmount, 0);
  const totalInterest = positions.reduce((sum, p) => sum + p.totalInterestReceived, 0);
  const averageYield = weightedAverageYield(positions);
  const yieldDescription = averageYieldDescription(
    averageYield.activeCount,
    averageYield.reportingCount,
  );

  let metrics;
  if (isLoading) {
    metrics = Array.from({ length: 4 }).map((_, i) => (
      <div key={i} className="bg-surface p-6 rounded-sm shadow-sm">
        <Skeleton className="h-3 w-24 mb-3" />
        <Skeleton className="h-8 w-16" />
      </div>
    ));
  } else if (error) {
    // The read failed. Zeros here would present an unread book as a
    // measured empty one.
    metrics = (
      <>
        <MetricCard label="Total Positions" value={ABSENT} caption={METRIC_READ_CAPTIONS.failed} />
        <MetricCard label="Total Committed" value={ABSENT} caption={METRIC_READ_CAPTIONS.failed} />
        <MetricCard label="Interest Earned" value={ABSENT} caption={METRIC_READ_CAPTIONS.failed} />
        <MetricCard label="Average Yield" value={ABSENT} caption={METRIC_READ_CAPTIONS.failed} />
      </>
    );
  } else {
    metrics = (
      <>
        <MetricCard
          label="Total Positions"
          value={formatCount(totalItems ?? positions.length)}
        />
        <MetricCard
          label="Total Committed"
          value={formatMoneyCompact(totalCommitted)}
          description={scopeNote}
        />
        <MetricCard
          label="Interest Earned"
          value={formatMoneyCompact(totalInterest)}
          // totalInterestReceived: "Cumulative interest CRT minted to this
          // position" (loan-syndication-protocol.openapi.yaml).
          description={scopeNote ?? 'Interest paid as CRT, cumulative'}
        />
        <MetricCard
          label="Average Yield"
          value={averageYield.value === null ? ABSENT : formatRatioAsPercent(averageYield.value)}
          description={scopeNote ? `${yieldDescription}. ${scopeNote}.` : yieldDescription}
        />
      </>
    );
  }

  let list;
  if (error) {
    list = (
      <Alert type="error" title="Participation positions could not be loaded">
        <p className="mb-3">{error.message}</p>
        <Button variant="secondary" onClick={() => refetch()} disabled={isFetching}>
          Retry
        </Button>
      </Alert>
    );
  } else if (isLoading) {
    list = (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-32 w-full" />
        ))}
      </div>
    );
  } else if (positions.length === 0) {
    list = (
      <EmptyState
        title="No participation positions"
        // Positions are created when the lead activates an offering
        // (offering.service.ts activate), not when an institution commits.
        description="A position appears here when an offering originated in the Aaim platform tenant is activated; the lead and each committed institution receive one."
      />
    );
  } else {
    list = (
      <div className="space-y-4">
        {positions.map((position) => (
          <PositionCard key={position.positionId} position={position} />
        ))}
        {partial && (
          <BodyText variant="ui" className="text-sm text-tertiary">
            Showing the first {formatCount(positions.length)} of {formatCount(totalItems)} positions.
          </BodyText>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* The read is the caller's own tenant (GET /positions); the page says
          so instead of presenting that book as the network. */}
      <BodyText variant="ui" className="text-sm text-tertiary mb-4">
        {POSITIONS_SCOPE_NOTE}
      </BodyText>

      {/* Summary Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {metrics}
      </div>

      {list}
    </div>
  );
}
